import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import Button from '@liquidweb-dev/undercurrent/Button';
import CardLayeredFooter, {
	CardLayeredFooterProps,
} from '@liquidweb-dev/undercurrent/CardLayeredFooter';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import LoaderCircle from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import CheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/Check';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import Link from '@liquidweb-dev/undercurrent/Link';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import type { Theme } from '@liquidweb-dev/undercurrent/styles';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import toast from '@liquidweb-dev/undercurrent/toast';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

import { useInput } from 'components/LWForm';
import useAssetControlPanelCredentialsSet from 'modules/queries/asset/controlPanel/credentials/useSet';
import useSslCertificateHostServers from 'modules/queries/ssl/certificate/useHostSevers';

interface CredentialsNoteContextValue {
	apiToken: string;
	hasCredentials: boolean;
	hasSniEnabled: boolean;
	isEditing: boolean;
	isError: boolean;
	isLoading: boolean;
	saveCredentials: () => void;
	setApiToken: (apiToken: string) => void;
	toggleEdit: () => void;
}

const credentialsNoteContext =
	createContext<CredentialsNoteContextValue | null>(null);

const useCredentialsNote = () => {
	const context = useContext(credentialsNoteContext);
	if (!context) {
		throw new Error(
			'useCredentialsNote must be used within credentialsNoteContext.Provider',
		);
	}

	return context;
};

const Icon = ({ sx, ...rest }: LucideSvgProps) => {
	const { hasCredentials, hasSniEnabled, isError, isLoading } =
		useCredentialsNote();

	if (isLoading) {
		return (
			<LoaderCircle
				sx={combineSx(
					({ size, vars }) => ({
						color: vars.palette.uc.highlight.icon.strong,
						height: size.xS,
						width: size.xS,
					}),
					sx,
				)}
				{...rest}
			/>
		);
	}

	if (isError) {
		return (
			<XIcon
				sx={combineSx(
					({ size, vars }) => ({
						color: vars.palette.uc.danger.border.strong,
						height: size.xS,
						width: size.xS,
					}),
					sx,
				)}
				{...rest}
			/>
		);
	}

	if (!hasCredentials) {
		return (
			<TriangleAlertIcon
				sx={combineSx(
					({ size, vars }) => ({
						color: vars.palette.uc.warning.icon.strong,
						height: size.xS,
						width: size.xS,
					}),
					sx,
				)}
				{...rest}
			/>
		);
	}

	if (!hasSniEnabled) {
		return (
			<XIcon
				sx={combineSx(
					({ size, vars }) => ({
						color: vars.palette.uc.danger.border.strong,
						height: size.xS,
						width: size.xS,
					}),
					sx,
				)}
				{...rest}
			/>
		);
	}

	return (
		<CheckIcon
			sx={combineSx(
				({ size, vars }) => ({
					color: vars.palette.uc.success.icon.strong,
					height: size.xS,
					width: size.xS,
				}),
				sx,
			)}
			{...rest}
		/>
	);
};

const ViewDocs = () => (
	<Stack sx={{ flexDirection: 'row', gap: 100, alignItems: 'center' }}>
		<Link
			component="a"
			href="https://docs.cpanel.net/whm/clusters/remote-access-key/"
			target="_blank"
			variant="label-lg"
			sx={{ fontWeight: 500 }}
		>
			View docs
		</Link>
		<ArrowUpRightIcon
			sx={({ size }) => ({ height: size.xS, width: size.xS })}
		/>
	</Stack>
);

const EditToggle = () => {
	const { toggleEdit } = useCredentialsNote();

	return (
		<IconButton size="small" variant="barebones" onClick={toggleEdit}>
			<PencilIcon />
		</IconButton>
	);
};

const ApiTokenForm = () => {
	const { apiToken, isLoading, saveCredentials, setApiToken } =
		useCredentialsNote();

	return (
		<Stack sx={{ flexDirection: 'row', gap: 150, mt: 150 }}>
			<TextField
				value={apiToken}
				onChange={({ target }) => setApiToken(target.value)}
				type="password"
				autoComplete="off"
				disabled={isLoading}
				placeholder="Paste API token here"
				sx={{ flexGrow: 1 }}
			/>
			<Button
				size="large"
				startIcon={<PlusIcon />}
				loading={isLoading}
				onClick={saveCredentials}
				sx={{ flexShrink: 0 }}
			>
				{isLoading ? 'Adding' : 'Add'}
			</Button>
		</Stack>
	);
};

const Message = () => {
	const { hasCredentials, hasSniEnabled, isError, isLoading } =
		useCredentialsNote();

	if (isLoading) return <>Adding token...</>;
	if (isError) return <>That API token didn&apos;t work, try another one</>;
	if (!hasCredentials)
		return <>No credentials available, add a WHM API token to continue</>;
	if (!hasSniEnabled)
		return (
			<>
				SNI is not installed on this server. Make sure your API token is correct
				and try again.
			</>
		);

	return <>Credentials available</>;
};

const Action = () => {
	const { hasCredentials, hasSniEnabled, isError, isLoading } =
		useCredentialsNote();

	if (isLoading) return null;

	if (!hasSniEnabled)
		return (
			<Stack
				sx={{
					flexDirection: 'row',
					gap: 100,
					alignItems: 'center',
					flexShrink: 0,
				}}
			>
				<Link
					component="a"
					href="https://www.liquidweb.com/help-docs/control-panel/whm/security/api-tokens-for-remote-access-in-whm/"
					target="_blank"
					variant="label-lg"
					sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
				>
					Learn more
				</Link>
				<ArrowUpRightIcon
					sx={({ size }) => ({ height: size.xS, width: size.xS })}
				/>
			</Stack>
		);

	if (isError) return <ViewDocs />;

	if (!hasCredentials) return <ViewDocs />;

	return <EditToggle />;
};

interface getBackgroundColorParams {
	loading?: boolean;
	danger?: boolean;
	success?: boolean;
	warning?: boolean;
}

const getBackgroundColor =
	({ loading, danger, success, warning }: getBackgroundColorParams) =>
	({ vars }: Theme) => {
		if (loading) {
			return vars.palette.uc.highlight.bg.main;
		}

		if (success) {
			return vars.palette.uc.success.bg.main;
		}

		if (danger) {
			return vars.palette.uc.danger.bg.main;
		}

		if (warning) {
			return vars.palette.uc.warning.bg.main;
		}

		return vars.palette.uc.success.bg.main;
	};

export const useSelectedServer = () => {
	const { value: serverValue } = useInput('server');

	const { data: hostServers, ...rest } = useSslCertificateHostServers();

	const selectedServer = useMemo(() => {
		return Object.values(hostServers || {}).find(
			(server) => server.uniq_id === serverValue,
		);
	}, [hostServers, serverValue]);

	const hasSniEnabled = fromApiBoolean(selectedServer?.sniEnabled);
	const hasCredentials = fromApiBoolean(selectedServer?.cpCredentials);

	return {
		hostServers,
		serverValue: typeof serverValue === 'string' ? serverValue : '',
		hasSniEnabled,
		hasCredentials,
		...rest,
	};
};

export type ServerCredentialsNoteProps = Omit<
	CardLayeredFooterProps,
	'children'
>;

const ServerCredentialsNote = ({ sx, ...rest }: ServerCredentialsNoteProps) => {
	const [apiToken, setApiToken] = useState('');
	const [isEditing, setEditing] = useState(false);

	const { serverValue, hasCredentials, hasSniEnabled } = useSelectedServer();

	// initialize display of mini form
	useEffect(() => {
		setEditing(!hasSniEnabled || !hasCredentials);
	}, [hasCredentials, hasSniEnabled]);

	const {
		mutate: setControlPanel,
		isError,
		isLoading,
		reset,
	} = useAssetControlPanelCredentialsSet();

	// clear mutation state upon server change
	useEffect(() => {
		if (serverValue) {
			reset();
		}
	}, [reset, serverValue]);

	const saveCredentials = useCallback(() => {
		setControlPanel(
			{
				control_panel: 'whm',
				auth_type: 'api_token',
				credentials: apiToken,
				uniq_id: serverValue,
			},
			{
				onSuccess: () => {
					setApiToken('');
					setEditing(false);
					toast.success('API token added', {
						description: 'Your WHM API token has successfully been added',
					});
				},
			},
		);
	}, [apiToken, serverValue, setControlPanel]);

	const contextValue = useMemo(
		() => ({
			apiToken,
			hasCredentials,
			hasSniEnabled,
			isEditing,
			isError,
			isLoading,
			saveCredentials,
			setApiToken,
			toggleEdit: () => setEditing((prev) => !prev),
		}),
		[
			apiToken,
			hasCredentials,
			hasSniEnabled,
			isEditing,
			isError,
			isLoading,
			saveCredentials,
		],
	);

	return (
		<credentialsNoteContext.Provider value={contextValue}>
			<CardLayeredFooter
				sx={combineSx(
					{
						display: 'flex',
						flexDirection: 'column',
						paddingX: 200,
						marginTop: -200,
						paddingTop: 350,
						paddingBottom: 150,
						backgroundColor: getBackgroundColor({
							danger: isError || !hasSniEnabled,
							loading: isLoading,
							success: hasCredentials && hasSniEnabled,
							warning: !hasCredentials,
						}),
						transition: 'all 200ms ease-in-out',
					},
					sx,
				)}
				{...rest}
			>
				<Stack
					sx={{
						flexDirection: 'row',
						gap: 200,
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Stack sx={{ flexDirection: 'row', gap: 100, alignItems: 'center' }}>
						<Icon sx={{ flexShrink: 0 }} />
						<Typography variant="body-sm">
							<Message />
						</Typography>
					</Stack>
					<Action />
				</Stack>

				<Collapse in={isEditing}>
					<ApiTokenForm />
				</Collapse>
			</CardLayeredFooter>
		</credentialsNoteContext.Provider>
	);
};

export default ServerCredentialsNote;
